import React, { useState } from 'react';
import { Check, Star, Zap, Shield, School, Rocket, Building2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PopupExample from './WaitingListPopup';
import { toast, ToastContainer } from 'react-toastify';

const PricingCard = ({
  title,
  price,
  period,
  description,
  features,
  buttonText,
  isPopular,
  icon: Icon,
  onButtonClick,
  badge
}) => (
  <div
    className={`relative flex flex-col h-full p-6 rounded-2xl transition-all duration-300 ${isPopular
      ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-2xl scale-105 z-10 border-2 border-blue-400'
      : 'bg-white text-gray-900 shadow-lg hover:shadow-2xl border-2 border-gray-100 hover:border-blue-200'
      }`}
  >
    {badge && (
      <div className="absolute top-0 right-0 -mt-3 -mr-3">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md ${isPopular
          ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-blue-900 animate-pulse'
          : 'bg-green-100 text-green-700'
          }`}>
          {badge}
        </span>
      </div>
    )}

    <div className="mb-4">
      <div className={`p-2 rounded-xl w-fit mb-3 ${isPopular ? 'bg-white/20' : 'bg-blue-50'}`}>
        <Icon className={`w-6 h-6 ${isPopular ? 'text-white' : 'text-blue-600'}`} />
      </div>
      <h3 className={`text-xl font-bold mb-1 ${isPopular ? 'text-white' : 'text-blue-900'}`}>{title}</h3>
      <p className={`text-xs mb-4 min-h-[2.5rem] ${isPopular ? 'text-blue-100' : 'text-gray-500'}`}>
        {description}
      </p>

      <div className="flex items-baseline mb-2">
        <span className="text-3xl font-bold tracking-tight">
          {typeof price === 'string' && price.includes('html') ? (
            <span dangerouslySetInnerHTML={{ __html: price }} />
          ) : (
            price
          )}
        </span>
        {period && (
          <span className={`ml-1 text-xs font-medium ${isPopular ? 'text-blue-100' : 'text-gray-500'}`}>
            /{period}
          </span>
        )}
      </div>
    </div>

    <div className="flex-grow">
      <div className={`w-full h-px mb-4 ${isPopular ? 'bg-blue-400' : 'bg-gray-200'}`} />
      <ul className="space-y-2 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPopular ? 'text-yellow-300' : 'text-green-600'}`} />
            <span className={`text-sm leading-tight ${isPopular ? 'text-blue-50' : 'text-gray-600'}`}>
              {feature}
            </span>
          </li>
        ))}
      </ul>
    </div>

    <button
      onClick={onButtonClick}
      className={`w-full py-3 px-4 rounded-lg font-bold text-sm transition-all duration-200 transform hover:-translate-y-0.5 ${isPopular
        ? 'bg-white text-blue-600 hover:bg-blue-50 shadow-md'
        : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm hover:shadow-md'
        }`}
    >
      {buttonText}
    </button>
  </div>
);

const PricingSection = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [pro, setPro] = useState("");

  const handleContactSales = (planName) => {
    setPro(planName);
    setIsOpen(true);
  };

  // Preview plans - one from each category
  const previewPlans = [
    {
      title: 'Pro Creator',
      price: '$15',
      period: 'month',
      description: 'For startups and indie developers.',
      icon: Rocket,
      features: [
        '1000 rows per generation',
        '200,000 data points/month',
        'Web search integration',
        'API access (10K calls/month)',
        'Custom dataset formats'
      ],
      buttonText: 'View Startup Plans',
      onButtonClick: () => navigate('/pricing/all'),
      isPopular: true,
      badge: 'Most Popular'
    },
    {
      title: 'Student Plus',
      price: '$5',
      period: 'month',
      description: 'Build impressive projects that stand out.',
      icon: School,
      features: [
        '1,000 rows per generation',
        '25,000 data points/month',
        'Unlimited generations',
        'Web search integration',
        'File upload (2MB)'
      ],
      buttonText: 'View Student Plans',
      onButtonClick: () => navigate('/pricing/all'),
      isPopular: false,
      badge: 'For Students'
    },
    {
      title: 'Dedicated Service',
      price: '$200+',
      period: 'one-time',
      description: 'Done-for-you datasets for serious projects.',
      icon: Shield,
      features: [
        '10K-150K rows custom dataset',
        'Dedicated team',
        'Expert human review',
        'Fine-tuning guidance',
        'Private server deployment'
      ],
      buttonText: 'View All Services',
      onButtonClick: () => navigate('/pricing/all'),
      isPopular: false,
      badge: 'Done For You'
    }
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-hidden">
      <PopupExample hidden={isOpen} isOpen={isOpen} setIsOpen={setIsOpen} pro={pro} toast={toast} />

      <div className="max-w-7xl mx-auto relative">
        <div className="text-center mb-12 relative z-10">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            From students to enterprises, we have the perfect plan for you.
          </p>

          {/* Category Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
              <Rocket className="w-4 h-4 mr-2" />
              Startups & Developers
            </div>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold">
              <School className="w-4 h-4 mr-2" />
              Students
            </div>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold">
              <Building2 className="w-4 h-4 mr-2" />
              Enterprise
            </div>
          </div>
        </div>

        {/* Preview Cards */}
        <div className="grid md:grid-cols-3 gap-8 items-stretch relative z-10 mb-12">
          {previewPlans.map((plan, index) => (
            <div key={index} className="h-full">
              <PricingCard {...plan} />
            </div>
          ))}
        </div>

        {/* View Full Pricing CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate('/pricing/all')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            View Full Pricing Details
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-gray-500 text-sm mt-4">
            See all tiers, managed services, and ecosystem partner benefits
          </p>
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={3000} />
    </section>
  );
};

export default PricingSection;